import { DesktopLoginPage } from '@/pages/DesktopLoginPage'
import { desktopLoginFromLocation } from '@/lib/desktopLogin'
import { ForumChannel } from '@/components/guild/ForumChannel'
import { applyForumEvent } from '@/lib/forums'
import { useQueryClient } from '@tanstack/react-query'
import { applySavedEvent } from '@/lib/savedMessages'
import { applyInboxDeletion } from '@/lib/inbox'
import { applyPinnedDeletion } from '@/lib/bulkDelete'
import { useCallback, useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import { Hash, X } from 'lucide-react'
import { GuildRail } from '@/components/GuildRail'
import { ChannelSidebar } from '@/components/ChannelSidebar'
import { MemberList } from '@/components/MemberList'
import { HomeSidebar } from '@/components/home/HomeSidebar'
import { SavedMessagesPage } from '@/components/home/SavedMessagesPage'
import { InboxPage } from '@/components/home/InboxPage'
import { FriendsPage } from '@/components/home/FriendsPage'
import { GroupPanel } from '@/components/chat/GroupPanel'
import { DMProfilePanel } from '@/components/chat/DMProfilePanel'
import { CollectionPage } from '@/components/home/CollectionPage'
import { DMChat } from '@/components/chat/DMChat'
import { ChannelChat } from '@/components/chat/ChannelChat'
import { UserPanel } from '@/components/UserPanel'
import { ModerationWorkspace } from '@/components/moderation/ModerationWorkspace'
import { SettingsLayer } from '@/components/settings/SettingsLayer'
import { GuildSettingsLayer } from '@/components/guild/GuildSettingsLayer'
import { InvitePage } from '@/components/guild/InvitePage'
import { AuthorizePage } from '@/components/guild/AuthorizePage'
import { SearchResults } from '@/components/guild/SearchResults'
import { ThreadsPanel } from '@/components/guild/ThreadsPanel'
import { IncomingCall } from '@/components/voice/IncomingCall'
import { AchievementToast } from '@/components/AchievementToast'
import { Announcements } from '@/components/releases/Announcements'
import { useAchievementsStore } from '@/store/achievements'
import type { Unlock } from '@/lib/achieve'
import { UserContextMenu } from '@/components/UserContextMenu'
import { QuickSwitcher } from '@/components/QuickSwitcher'
import { DiscoveryPage } from '@/components/discovery/DiscoveryPage'
import { PublicDiscovery } from '@/components/discovery/PublicDiscovery'
import { LandingPage } from '@/pages/LandingPage'
import { AuthPage } from '@/pages/AuthPage'
import { EmailActionPage } from '@/pages/EmailActionPage'
import { GatewayClient } from '@/lib/gateway'
import { currentAccessToken, type CurrentUser } from '@/lib/auth'
import { setGatewayClient } from '@/lib/gatewaySingleton'
import { startGameStatus } from '@/lib/gameStatus'
import { startIdleWatch } from '@/lib/idle'
import { onMessageCreate, openPath, readPrefs, setTitleBadge } from '@/lib/notify'
import { interceptAppLinks, openChannelLink } from '@/lib/navigate'
import { useHotkeys } from '@/lib/useHotkeys'
import { DM_SETTINGS, ownMuted } from '@/lib/muting'
import { useIsMobile } from '@/lib/mobile'
import { useChatViewport } from '@/lib/chatViewport'
import { Modal } from '@/components/settings/Modal'
import { syncPush } from '@/lib/push'
import { useAppStore, authPathFromLocation, publicPageFromLocation, discordCodeFromLocation, emailActionFromLocation } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { colorUnderWindowControls } from '@/lib/titlebarColor'
import { AppTitlebar } from '@/components/AppTitlebar'
import { stopMetrika } from '@/lib/metrika'
import { desktop, isDesktop } from '@/lib/desktop'
import { guildApi } from '@/lib/guildApi'
import { useSocialStore } from '@/store/social'
import { useGuildStore } from '@/store/guilds'
import { useVoiceStore } from '@/store/voice'
import { usePresenceStore } from '@/store/presence'
import { ChannelType, type MemberListUpdate, type Message, type PresenceUpdate, type ReadyPayload, type User, type UserSettings } from '@/types/api'
import { StatsPage } from '@/pages/stats/StatsPage'
import chatStyles from '@/components/ChatView.module.css'
import styles from './App.module.css'

export function App() {
  useChatViewport()
  const theme = useAppStore((s) => s.theme)
  const status = useAuthStore((s) => s.status)
  const bootstrap = useAuthStore((s) => s.bootstrap)
  const pendingInvite = useAppStore((s) => s.pendingInvite)
  const pendingAuthorize = useAppStore((s) => s.pendingAuthorize)
  const pendingLink = useAppStore((s) => s.pendingLink)

  const [publicPage, setPublicPage] = useState(publicPageFromLocation)
  // что показать гостю: главную, форму входа или публичную витрину
  const [authMode, setAuthMode] = useState(authPathFromLocation)
  // ссылка из письма: подтверждение почты или смена пароля — и гостю, и вошедшему
  const [emailAction, setEmailAction] = useState(emailActionFromLocation)
  const [desktopLogin, setDesktopLogin] = useState(desktopLoginFromLocation)

  useLayoutEffect(() => {
    if (status !== 'anonymous' || publicPage || emailAction || desktopLogin) stopMetrika()
  }, [status, publicPage, emailAction, desktopLogin])

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    if (desktop) document.documentElement.dataset.desktop = desktop.platform
  }, [theme])

  /*
   * Windows рисует кнопки окна поверх интерфейса. Красим их в цвет того, что под ними сейчас
   * (lib/titlebarColor.ts): шапка чата, полоса над списком участников, настройки, затемнение под
   * окном — у всех свой фон. Сверяем раз в 400 мс и при смене размера, в приложение шлём, только
   * когда цвет поменялся.
   */
  useEffect(() => {
    if (desktop?.platform !== 'win32' || !desktop.setTitlebar) return
    const setTitlebar = desktop.setTitlebar
    let last = ''
    const sync = () => {
      const color = colorUnderWindowControls()
      const symbol = getComputedStyle(document.documentElement).getPropertyValue('--interactive-normal').trim() || '#babbc0'
      if (!color || `${color}|${symbol}` === last) return
      last = `${color}|${symbol}`
      setTitlebar(color, symbol)
    }
    sync()
    const timer = window.setInterval(sync, 400)
    window.addEventListener('resize', sync)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('resize', sync)
    }
  }, [theme])

  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  if (status === 'loading') return <Splash />
  if (emailAction) {
    return <EmailActionPage action={emailAction} onDone={() => { history.replaceState(null, '', '/'); setEmailAction(null) }} />
  }
  if (desktopLogin) {
    if (status === 'anonymous') return <AuthPage initial={authMode === 'register' ? 'register' : 'login'} desktopApproval />
    return <DesktopLoginPage id={desktopLogin} onDone={() => { history.replaceState(null, '', '/'); setDesktopLogin(null) }} />
  }
  if (status === 'anonymous') {
    // каталог и карточка сервера открываются без входа: /discovery и /s/<slug>
    if (publicPage) {
      return (
        <PublicDiscovery
          page={publicPage}
          onLogin={() => { setPublicPage(null); setAuthMode('login') }}
          onBack={() => setPublicPage(null)}
        />
      )
    }
    // В приложении лендинг не нужен: человек уже скачал программу, и предлагать ему
    // «скачать приложение» и «войти в браузере» — издевательство. Сразу форма входа.
    if (isDesktop) {
      return <AuthPage initial={authMode === 'register' ? 'register' : 'login'} onCatalog={() => setPublicPage({ kind: 'catalog' })} />
    }
    // по приглашению, ссылке на канал и авторизации приложения сразу нужна форма входа, иначе — главная
    if (pendingInvite || pendingAuthorize || pendingLink || authMode) {
      return <AuthPage initial={authMode === 'register' ? 'register' : 'login'} onHome={() => setAuthMode(null)} />
    }
    return (
      <LandingPage
        onLogin={() => setAuthMode('login')}
        onRegister={() => setAuthMode('register')}
        onCatalog={() => setPublicPage({ kind: 'catalog' })}
      />
    )
  }
  // статистика для команды — отдельная страница без шлюза: смотрящий на цифры в них не попадает
  if (window.location.pathname === '/stats') return <StatsPage />
  return <Shell />
}

function Shell() {
  const queryClient = useQueryClient()
  const mobile = useIsMobile()
  const view = useAppStore((s) => s.view)
  const setView = useAppStore((s) => s.setView)
  const pane = useAppStore((s) => s.pane)
  const showMembers = useAppStore((s) => s.showMembers)
  const toggleMembers = useAppStore((s) => s.toggleMembers)
  const voiceChatChannelId = useAppStore((s) => s.voiceChatChannelId)
  const setVoiceChatChannelId = useAppStore((s) => s.setVoiceChatChannelId)
  const currentChannel = useGuildStore((s) => view.kind === 'guild' && view.channelId ? s.channels[view.guildId]?.[view.channelId] : undefined)
  const forum = useGuildStore(s => view.kind === 'guild' && currentChannel ? currentChannel.type === ChannelType.GuildForum ? currentChannel : currentChannel.type === ChannelType.PublicThread && currentChannel.parent_id && s.channels[view.guildId]?.[currentChannel.parent_id]?.type === ChannelType.GuildForum ? s.channels[view.guildId]?.[currentChannel.parent_id] : undefined : undefined)
  const voiceChatOpen = currentChannel?.type === ChannelType.GuildVoice && currentChannel.id === voiceChatChannelId
  const closeVoiceChat = () => {
    setVoiceChatChannelId(null)
    requestAnimationFrame(() => document.getElementById(`voice-chat-button-${currentChannel?.id}`)?.focus())
  }
  // открыта группа в личных сообщениях — её участники идут в ту же правую колонку, что у сервера;
  // беседа один на один — профиль собеседника, как в Discord
  const dmChannel = useSocialStore((s) => (view.kind === 'home' && 'channelId' in view ? s.channels[view.channelId] : undefined))
  const groupChannel = dmChannel?.type === ChannelType.GroupDM ? dmChannel : undefined
  const dmRecipient = dmChannel?.type === ChannelType.DM ? dmChannel.recipients?.[0] : undefined
  const dmSearch = useAppStore((s) => s.dmSearch)
  const setDMSearch = useAppStore((s) => s.setDMSearch)
  const closeDMSearch = () => {
    setDMSearch(null)
    requestAnimationFrame(() => document.getElementById(`dm-search-button-${dmChannel?.id}`)?.focus())
  }
  const searchQuery = useAppStore((s) => s.searchQuery)
  const setSearchQuery = useAppStore((s) => s.setSearchQuery)
  const threadsPanel = useAppStore((s) => s.threadsPanel)
  const setThreadsPanel = useAppStore((s) => s.setThreadsPanel)
  const guildSettings = useAppStore((s) => s.guildSettings)
  const openGuildSettings = useAppStore((s) => s.openGuildSettings)
  const pendingInvite = useAppStore((s) => s.pendingInvite)
  const pendingAuthorize = useAppStore((s) => s.pendingAuthorize)
  const setGateway = useAppStore((s) => s.setGateway)
  const setCurrentUser = useAppStore((s) => s.setCurrentUser)
  const signOut = useAuthStore((s) => s.signOut)
  const guildExists = useGuildStore((s) => (view.kind === 'guild' ? Boolean(s.guilds[view.guildId]) : true))
  const gateway = useAppStore((s) => s.gateway)
  const linkDenied = useAppStore((s) => s.linkDenied)
  const setLinkDenied = useAppStore((s) => s.setLinkDenied)
  const closeLinkDenied = useCallback(() => setLinkDenied(false), [setLinkDenied])
  // горячие клавиши (микрофон, звук, быстрый переход) — пока открыт клиент
  useHotkeys()

  useEffect(() => {
    // Vite проксирует /gateway на ws://localhost:8081 (vite.config.ts); в проде — тот же путь через ingress
    const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/gateway`
    const client = new GatewayClient(url, currentAccessToken)
    setGatewayClient(client)
    const social = useSocialStore.getState()
    const guilds = useGuildStore.getState()
    client.onStatus = setGateway
    // первый READY или уже после переподключения
    let readyBefore = false
    const offReady = client.on('READY', (d) => {
      const ready = d as ReadyPayload
      setCurrentUser(ready.user)
      useVoiceStore.getState().setSelf(ready.user.id)
      useSocialStore.getState().applyReady(ready)
      useGuildStore.getState().applyReady(ready.guilds ?? [], ready.user_guild_settings ?? [])
      usePresenceStore.getState().applySettings(ready.user_settings)
      usePresenceStore.getState().ingest(ready.presences ?? [])
      // Новый READY означает, что replay недоступен: перечитать загруженные сообщения (открытый чат — сразу).
      if (readyBefore) useSocialStore.getState().markStale()
      applySavedEvent(queryClient, ready.user.id, 'READY', ready)
      applyForumEvent(queryClient, ready.user.id, 'READY', ready)
      readyBefore = true
    })
    const offAll = client.on('*', (e) => {
      const { t, d } = e as { t: string; d: unknown }
      if (t === 'READY') return
      applySavedEvent(queryClient, useAuthStore.getState().user?.id, t, d)
      applyInboxDeletion(queryClient, useAuthStore.getState().user?.id, t, d)
      applyPinnedDeletion(queryClient, useAuthStore.getState().user?.id, t, d)
      applyForumEvent(queryClient, useAuthStore.getState().user?.id, t, d, useGuildStore.getState().channels)
      if (t === 'USER_UPDATE' || t === 'GUILD_MEMBER_UPDATE') {
        const changed = d as { id?: string; user?: { id: string } }
        const id = t === 'USER_UPDATE' ? changed.id : changed.user?.id
        if (id) void queryClient.invalidateQueries({ queryKey: ['profile', id] })
      }
      if (t === 'USER_UPDATE') {
        // свой профиль, изменённый с другого устройства
        const u = d as User & { profile_effect?: CurrentUser['profile_effect'] }
        const cur = useAuthStore.getState().user
        if (cur && u.id === cur.id) {
          useAuthStore.getState().setUser({
            ...cur, username: u.username, global_name: u.global_name, avatar: u.avatar,
            avatar_decoration_data: u.avatar_decoration_data ?? null, display_name_styles: u.display_name_styles ?? null, profile_effect: u.profile_effect ?? null,
          })
        }
      }
      if (t === 'ACHIEVEMENT_UNLOCK') useAchievementsStore.getState().push(d as Unlock)
      useSocialStore.getState().applyEvent(t, d)
      useGuildStore.getState().applyEvent(t, d)
      useVoiceStore.getState().applyEvent(t, d)
      if (t === 'MESSAGE_CREATE') onMessageCreate(d as Message)
      // свой статус и чем заняты другие: меню статуса на соседнем устройстве, строка под именем
      if (t === 'PRESENCE_UPDATE') usePresenceStore.getState().ingest([d as PresenceUpdate])
      if (t === 'USER_SETTINGS_UPDATE') usePresenceStore.getState().applySettings(d as UserSettings)
      if (t === 'GUILD_MEMBER_LIST_UPDATE') {
        const items = (d as MemberListUpdate).ops.flatMap((op) => op.items)
        usePresenceStore.getState().ingest(items.flatMap((it) => (it.member ? [it.member.presence] : [])))
      }
    })
    const offAuth = client.on('auth_failed', () => void signOut())
    // статус игры шлём только из приложения: браузер не знает, что запущено на компьютере
    let stopGame: (() => void) | null = null
    const offGameReady = client.on('READY', () => {
      stopGame?.()
      stopGame = startGameStatus()
    })
    // «неактивен» после 10 минут без ввода; звонок и игра считаются присутствием
    const stopIdle = startIdleWatch(
      (idle) => {
        usePresenceStore.getState().setAutoIdle(idle)
        client.setIdle(idle)
      },
      () => Boolean(useVoiceStore.getState().connection) || client.playing,
    )
    client.connect()
    return () => {
      offReady()
      offAll()
      offAuth()
      offGameReady()
      stopIdle()
      stopGame?.()
      void useVoiceStore.getState().leave()
      client.close()
      setGatewayClient(null)
      setGateway('idle')
      social.reset()
      guilds.reset()
      usePresenceStore.getState().reset()
      useAppStore.getState().resetNavigation({ kind: 'home', page: 'friends' })
    }
  }, [setGateway, setCurrentUser, signOut, queryClient])

  // сервер удалили или нас выгнали — возвращаемся домой
  useEffect(() => {
    if (view.kind === 'guild' && !guildExists && gateway === 'ready') setView({ kind: 'home', page: 'friends' })
  }, [view, guildExists, gateway, setView])

  // открыли по ссылке на канал или сообщение: после READY видно, есть ли доступ, и можно
  // загрузить окно вокруг сообщения; нет доступа — окно с объяснением и «Друзья»
  useEffect(() => {
    if (gateway !== 'ready') return
    const link = useAppStore.getState().pendingLink
    if (!link) return
    useAppStore.getState().setPendingLink(null)
    void openChannelLink(link).then((ok) => {
      if (!ok) setView({ kind: 'home', page: 'friends' })
    })
  }, [gateway, setView])

  // ссылки на Кличат в сообщениях открываются здесь же, а не в новой вкладке или браузере
  useEffect(() => interceptAppLinks(), [])

  // клик по push-уведомлению: service worker просит открыть канал
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; url?: string } | null
      if (data?.type === 'navigate' && data.url) openPath(data.url)
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [])

  // после входа подписка браузера привязывается к новой сессии
  useEffect(() => {
    if (gateway === 'ready' && readPrefs().push) void syncPush()
  }, [gateway])

  // вернулись из Discord: меняем код на доступ и открываем настройки нужного сервера
  useEffect(() => {
    const pending = discordCodeFromLocation()
    if (!pending) return
    history.replaceState(null, '', '/')
    void guildApi
      .discordLink(pending.code, pending.state)
      .then(({ guild_id }) => openGuildSettings(guild_id, 'import'))
      .catch(() => undefined)
  }, [openGuildSettings])

  // счётчик упоминаний в заголовке вкладки и на иконке приложения; заглушённые беседы не в счёт
  const dmSettings = useGuildStore((s) => s.settings[DM_SETTINGS])
  const mentionTotal = useSocialStore((s) => Object.values(s.readStates).reduce((n, rs) => n + (rs.mention_count && s.channels[rs.id] && ownMuted(dmSettings, rs.id) ? 0 : rs.mention_count), 0))
  useEffect(() => {
    setTitleBadge(mentionTotal)
    desktop?.setBadge(mentionTotal)
    return () => {
      setTitleBadge(0)
      desktop?.setBadge(0)
    }
  }, [mentionTotal])

  /*
   * Правая колонка: результаты поиска, ветки или участники — что-то одно.
   * На телефоне она становится отдельным слоем поверх чата, и ей нужна своя шапка
   * с крестиком: на широком экране её закрывает та же кнопка, что открыла.
   */
  const right: { title: string; close: () => void; node: ReactNode; layout?: 'profile' | 'voice-chat' | 'dm-search' } | null =
    view.kind === 'home'
      ? dmChannel && dmSearch?.channelId === dmChannel.id
        ? { title: 'Поиск по беседе', close: closeDMSearch, node: <SearchResults key={dmChannel.id} channelId={dmChannel.id} query={dmSearch.query} onClose={closeDMSearch} />, layout: 'dm-search' }
        : groupChannel && showMembers
        ? { title: 'Участники', close: toggleMembers, node: <GroupPanel channel={groupChannel} /> }
        : dmRecipient && showMembers
          ? { title: 'Профиль', close: toggleMembers, node: <DMProfilePanel key={dmRecipient.id} user={dmRecipient} />, layout: 'profile' }
          : null
      : view.kind !== 'guild'
      ? null
      : searchQuery
        ? { title: 'Результаты поиска', close: () => setSearchQuery(null), node: <SearchResults guildId={view.guildId} query={searchQuery} /> }
        : voiceChatOpen && currentChannel
          ? { title: 'Чат', close: closeVoiceChat, node: <ChannelChat key={currentChannel.id} guildId={view.guildId} channelId={currentChannel.id} embedded onClose={closeVoiceChat} />, layout: 'voice-chat' }
        : threadsPanel?.guildId === view.guildId
          ? { title: 'Ветки', close: () => setThreadsPanel(null), node: <ThreadsPanel guildId={view.guildId} channelId={threadsPanel.channelId} /> }
          : showMembers && !(forum && currentChannel?.id !== forum.id)
            ? { title: 'Участники', close: toggleMembers, node: <MemberList guildId={view.guildId} /> }
            : null

  return (
    <div className={styles.app} data-appbar={isDesktop && !mobile ? '' : undefined} data-view={view.kind} data-pane={pane} data-members={right ? 'on' : 'off'} data-right={right?.layout}>
      {isDesktop && !mobile ? <AppTitlebar mentions={mentionTotal} /> : null}
      {/* Две панели: навигация и содержимое. На широком экране обёртки прозрачны для сетки
          (display: contents), на телефоне видна одна из них — какая, решает data-pane. */}
      <div className={styles.navPane} inert={mobile && (right?.layout === 'voice-chat' || right?.layout === 'dm-search')}>
        <GuildRail />
        {view.kind === 'home' ? <HomeSidebar /> : view.kind === 'guild' ? <ChannelSidebar key={view.guildId} guildId={view.guildId} /> : null}
        {/* Панель пользователя лежит поверх колонок и идёт под обеими — как в Discord.
            В каталоге списка каналов нет, там ей не за что зацепиться. */}
        {view.kind === 'discovery' ? null : <UserPanel />}
      </div>
      <div className={styles.contentPane} inert={mobile && (right?.layout === 'voice-chat' || right?.layout === 'dm-search')}>
        {view.kind === 'discovery' ? (
          <DiscoveryPage key={view.openedAt ?? 'discovery'} />
        ) : view.kind === 'home' ? (
          'channelId' in view ? <DMChat key={view.channelId} channelId={view.channelId} /> : view.page === 'friends' ? <FriendsPage /> : view.page === 'inbox' ? <InboxPage /> : view.page === 'saved' ? <SavedMessagesPage /> : <CollectionPage key={view.page} page={view.page} />
        ) : view.channelId ? (
          forum ? <ForumChannel key={forum.id} guildId={view.guildId} channelId={forum.id} selectedThreadId={currentChannel?.id !== forum.id ? currentChannel?.id : undefined} /> : <ChannelChat key={view.channelId} guildId={view.guildId} channelId={view.channelId} />
        ) : (
          <EmptyChannel />
        )}
      </div>
      {right ? (
        <div className={styles.rightPane}>
          <div className={styles.rightHeader} hidden={right.layout === 'voice-chat'}>
            <span className={styles.rightTitle}>{right.title}</span>
            <button type="button" className={styles.rightClose} aria-label="Закрыть" onClick={right.close}><X size={22} /></button>
          </div>
          {right.node}
        </div>
      ) : null}
      <SettingsLayer />
      <ModerationWorkspace />
      <IncomingCall />
      <AchievementToast />
      <Announcements paused={Boolean(guildSettings || pendingInvite || pendingAuthorize || linkDenied)} />
      <UserContextMenu />
      <QuickSwitcher />
      {guildSettings ? <GuildSettingsLayer guildId={guildSettings.guildId} section={guildSettings.section} /> : null}
      {pendingInvite ? <InvitePage code={pendingInvite} /> : null}
      {pendingAuthorize ? <AuthorizePage params={pendingAuthorize} /> : null}
      {linkDenied ? (
        <Modal title="Нет доступа к этому каналу" onClose={closeLinkDenied}>
          <p className={styles.deniedText}>Канал удалён или скрыт от вас настройками сервера. Если вы не на этом сервере, нужно приглашение.</p>
          <div className={styles.deniedButtons}>
            <button type="button" className={styles.deniedButton} onClick={closeLinkDenied}>Понятно</button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

function EmptyChannel() {
  return (
    <main className={chatStyles.chat} aria-label="Нет канала">
      <div className={styles.emptyChannel}>
        <Hash size={48} />
        <p>Текстовых каналов пока нет. Создайте канал через меню сервера.</p>
      </div>
    </main>
  )
}

function Splash() {
  return (
    <div className={styles.splash} role="status" aria-live="polite">
      <div className={styles.splashLogo} aria-hidden="true" />
      <span>Загружаем…</span>
    </div>
  )
}
